/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 체크박스 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/check/Check.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 17.
 */
import * as parent from '@components/form/field/Field';
import * as check from '@elements/check/Check';
import { LanguageText } from '@utils/Language';
import './Check.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 선택항목명
     */
    boxLabel?: LanguageText;

    /**
     * 선택항목 라벨스타일
     */
    boxLabelStyle?: Partial<Omit<CSSStyleDeclaration, 'width' | 'height' | 'padding' | 'margin'>>;

    /**
     * 선택시 전송될 값
     */
    onValue?: string | boolean;

    /**
     * 미선택시 전송될 값
     */
    offValue?: string | boolean;

    /**
     * 선택여부
     */
    checked?: boolean;

    /**
     * 표시방법
     */
    displayType?: 'input' | 'box';

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Check extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'check';

    /**
     * 선택항목명
     */
    boxLabel: LanguageText | null;

    /**
     * 선택항목 라벨스타일
     */
    boxLabelStyle: Partial<Omit<CSSStyleDeclaration, 'width' | 'height' | 'padding' | 'margin'>> | null;

    /**
     * 선택시 전송될 값
     */
    onValue: string | boolean;

    /**
     * 미선택시 전송될 값
     */
    offValue: string | boolean | null;

    /**
     * 선택여부
     */
    checked: boolean;

    /**
     * 선택항목 출력방식
     */
    displayType: 'input' | 'box';

    /**
     * 체크박스 엘리먼트
     */
    check: check.Check = null;

    /**
     * 체크박스 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Check> = null) {
        super(properties);

        this.boxLabel = this.properties?.boxLabel ?? null;
        this.boxLabelStyle = this.properties?.boxLabelStyle ?? null;
        this.onValue = this.properties?.onValue ?? true;
        this.offValue = this.properties?.offValue ?? null;
        this.checked = this.properties?.checked ?? false;
        this.initValue = this.initValue ?? this.checked;
        this.displayType = this.properties?.displayType === 'box' ? 'box' : 'input';
    }

    /**
     * 체크박스 엘리먼트를 가져온다.
     *
     * @returns Check 엘리먼트
     */
    getCheck(): check.Check {
        if (this.check === null) {
            this.check = new check.Check({
                type: 'check',
                label: this.boxLabel,
                labelStyle: this.boxLabelStyle,
                listeners: {
                    blur: async (check) => {
                        await this.setValue(check.isChecked(), false);
                    },
                    change: async (_, checked) => {
                        const validation = this.getValidationHistory(checked, true);
                        this.updateValidation(validation);
                        await this.setValue(checked, false);
                    },
                },
            });
            this.check.setComponent(this);
        }

        return this.check;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     * @param is_update - INPUT 태그의 값을 변경할 지 여부
     */
    async setValue(value: boolean | string, is_init: boolean = false): Promise<void> {
        await this.lockValue();

        this.checked = value === true || value === this.onValue;

        this.getCheck().setValue(this.checked, false);

        await this.releaseValue(this.checked == true ? this.onValue : this.offValue, is_init);
    }

    /**
     * 체크상태를 가져온다.
     *
     * @returns 체크상태
     */
    isChecked(): boolean {
        return this.checked;
    }

    /**
     * 필드값이 비어있는지 확인한다.
     *
     * @returns 빈값여부
     */
    async isBlank(): Promise<boolean> {
        return this.checked === false;
    }

    /**
     * 읽기전용상태를 변경한다.
     *
     * @param readonly - 읽기전용여부
     */
    setReadonly(readonly: boolean): void {
        this.getCheck().setReadonly(readonly);
        super.setReadonly(readonly);
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.getCheck().setDisabled(disabled);
        return super.setDisabled(disabled);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.getCheck().$getElement());
        await this.getCheck().render();
    }
}
